import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Flame, CalendarClock, UserCheck, Star, ArrowUpRight, Search } from "lucide-react";
import JobCard from "@/components/jobs/JobCard";
import JobSearch from "@/components/jobs/JobSearch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_CONFIG = [
  { key: "hot", title: "Hot Jobs", subtitle: "High urgency openings closing soon", Icon: Flame, cls: "text-red-600", testId: "section-hot" },
  { key: "walkin", title: "Walk-in Jobs", subtitle: "Direct interviews — no waiting", Icon: CalendarClock, cls: "text-amber-600", testId: "section-walkin" },
  { key: "fresher", title: "Freshers Jobs", subtitle: "Roles for 0-1 year experience", Icon: UserCheck, cls: "text-emerald-600", testId: "section-fresher" },
];

export default function OpeningsExplorer() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", location: "all", department: "all", experience: "all" });

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/jobs?limit=50`)
      .then((r) => mounted && setJobs(r.data))
      .catch(() => mounted && setJobs([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const featured = useMemo(() => jobs.filter((j) => j.is_featured).slice(0, 4), [jobs]);
  const byCategory = useMemo(() => ({
    hot: jobs.filter((j) => j.category === "hot").slice(0, 4),
    walkin: jobs.filter((j) => j.category === "walkin").slice(0, 4),
    fresher: jobs.filter((j) => j.category === "fresher").slice(0, 4),
  }), [jobs]);

  const handleSearch = (v) => {
    const params = new URLSearchParams();
    if (v.q?.trim()) params.set("q", v.q.trim());
    if (v.location && v.location !== "all") params.set("location", v.location);
    if (v.department && v.department !== "all") params.set("department", v.department);
    if (v.experience && v.experience !== "all") params.set("experience", v.experience);
    navigate(`/careers${params.toString() ? `?${params.toString()}` : ""}`);
  };

  if (loading) {
    return <section className="py-20 text-center text-slate-500">Loading openings…</section>;
  }
  if (jobs.length === 0) return null;

  return (
    <section data-testid="openings-explorer" className="relative -mt-16 lg:-mt-20 pb-20 lg:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search bar floating over hero */}
        <div className="relative z-10">
          <div className="text-center mb-6 hidden lg:block">
            <p className="inline-block text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-primary bg-white/90 backdrop-blur px-3 py-1.5 rounded-full border border-white/50 shadow-sm">
              🔍 Find Your Dream Job
            </p>
          </div>
          <JobSearch value={filters} onChange={setFilters} onSubmit={handleSearch} variant="card" />
        </div>

        {/* Stats strip */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { v: jobs.length, l: "Live Openings" },
            { v: jobs.reduce((s, j) => s + (j.openings || 0), 0), l: "Total Vacancies" },
            { v: new Set(jobs.map((j) => j.department)).size, l: "Departments" },
            { v: new Set(jobs.map((j) => j.location)).size, l: "Locations" },
          ].map((s) => (
            <div key={s.l} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 text-center">
              <div className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">{s.v}</div>
              <div className="mt-1 text-[11px] sm:text-xs text-slate-500 uppercase tracking-wider font-semibold">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <div className="mt-16 lg:mt-20">
            <Header
              Icon={Star}
              iconCls="text-primary"
              title="Featured Openings"
              subtitle="Premium openings hand-picked by our recruitment team"
              testId="section-featured"
            />
            <div className="mt-8 grid md:grid-cols-2 gap-5 lg:gap-6">
              {featured.map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          </div>
        )}

        {/* Category rows */}
        {SECTION_CONFIG.map(({ key, title, subtitle, Icon, cls, testId }) => byCategory[key].length > 0 && (
          <div key={key} className="mt-16 lg:mt-20" data-testid={testId}>
            <Header Icon={Icon} iconCls={cls} title={title} subtitle={subtitle} testId={testId} />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {byCategory[key].map((j) => <JobCard key={j.id} job={j} variant="compact" />)}
            </div>
          </div>
        ))}

        {/* All jobs grid (latest) */}
        <div className="mt-16 lg:mt-20">
          <Header
            Icon={Search}
            iconCls="text-slate-700"
            title={`Latest ${Math.min(jobs.length, 12)} openings`}
            subtitle="Explore all live opportunities pan India"
            testId="section-latest"
          />
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {jobs.slice(0, 12).map((j) => <JobCard key={j.id} job={j} />)}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/careers"
              data-testid="view-all-jobs"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-primary transition-colors"
            >
              View All Jobs <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Header({ Icon, iconCls, title, subtitle, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 reveal">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 shrink-0 rounded-2xl bg-white border border-slate-200 flex items-center justify-center ${iconCls}`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <h2 data-testid={`${testId}-title`} className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 leading-tight tracking-tight">{title}</h2>
          <p className="mt-1.5 text-sm sm:text-base text-slate-600">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}
